import { FormattedMessage } from '@godaddy/react-mintl';
import { Columns, ColumnItem } from '@ux/column-layout';
import { TrayMenu, TrayTitle } from '@ux/tray-menu';
import golfToEid from '@gd-internal/golf-to-eid';
import text from '@ux/text';
import React from 'react';
import Link from '@ux/link';
import { URL } from '@ux/util';

import '@ux/tray-menu/styles';
import '@ux/column-layout/styles';

const classNames = (className) =>
  Array.isArray(className) ? className : [className];

const normalize = (pathname) => {
  if (!/^(https?:)?\/\//.test(pathname)) {
    pathname = pathname.charAt(0) === '/' ? pathname : '/' + pathname;
    pathname = pathname.replace('//', '/');
  }

  return pathname;
};

const urlMatches = (url1, url2, ignoreQueryStrings = false) => {
  if (typeof url1 !== 'string' || typeof url2 !== 'string') {
    return false;
  }

  if (ignoreQueryStrings) {
    url1 = new URL(url1, true).set('query', {}).href;
    url2 = new URL(url2, true).set('query', {}).href;
  }

  return url1.replace(/\/$/, '') === url2.replace(/\/$/, '');
};

const setActiveItem = (caption) => {
  if (typeof window !== 'undefined') {
    window.sessionStorage.setItem('activeCaption', caption);
  }
};

const linkBuilder = (opts) => {
  /* eslint-disable no-unused-vars */
  const {
    href, root = '', eidPrefix, active, activeClass,
    index, options, children, leftnav, onlyMobile, isOpen,
    ...linkProps
  } = opts;
  /* eslint-enable no-unused-vars */

  if (!linkProps.title || !href) {
    return null;
  }

  return (
    <Link
      { ...linkProps }
      href={ normalize(root + href) }
      data-eid={ linkProps.id && golfToEid(linkProps.id, {
        prefix: eidPrefix,
        postfix: 'link.click'
      }) }>
      <span>{ linkProps.title }</span>
    </Link>
  );
};

const renderLinkFlagInternal = (
  className,
  message,
  key,
  messageIsPreTranslated = false
) => (
  <span key={ key }>
    &nbsp;
    <span className={ className }>
      -&nbsp;
      { messageIsPreTranslated ? (
        <span>{ message }</span>
      ) : (
        <FormattedMessage id={ message } />
      ) }
    </span>
  </span>
);

const renderLinkFlag = (link, classes) => {
  if (!link.options) {
    return;
  }

  const ret = [];
  let i = 0;

  if (link.options.onSale) {
    ret.push(
      renderLinkFlagInternal(
        classes.onSale,
        'Shared:ProductNav:Flag:OnSale',
        i++
      )
    );
  }
  if (link.options.isNew) {
    ret.push(
      renderLinkFlagInternal(classes.isNew, 'Shared:ProductNav:Flag:New', i++)
    );
  }
  if (link.options.isFree) {
    ret.push(
      renderLinkFlagInternal(
        classes.isFree,
        'Shared:ProductNav:Flag:TryItFree',
        i++
      )
    );
  }
  if (link.options.isPro) {
    ret.push(<span className={ classes.isPro } key={ i++ }></span>);
  }
  if (link.options.tout || link.options.toutText) {
    ret.push(
      renderLinkFlagInternal(
        classes.tout,
        link.options.tout || link.options.toutText,
        i++,
        !link.options.tout && link.options.toutText
      )
    );
  }

  return ret;
};

const linkListBuilder = (items = [], options = {}) => {
  const { className, ...props } = options;
  const active = props.active || false;

  return items
    .map((item, i) => {
      const local = classNames(className);

      if (!item.title) return;

      if (active !== false) {
        if (
          item.id === active ||
          item.title === active ||
          urlMatches(active, item.href)
        ) {
          local.push(props.activeClass || '');
        }
      }

      const onClicked = setActiveItem.bind(null, item.id);

      return (
        <li
          key={ (item.id || '') + i }
          id={ item.id }
          className={ local.filter(Boolean).join(' ') }>
          { linkBuilder({
            ...item,
            ...props,
            index: i,
            onClicked
          }) }
        </li>
      );
    })
    .filter(Boolean);
};

// eslint-disable-next-line max-params
const trayColumnBuilder = (item, columnClassNames, index, urlArgs, suppressTitle) => {
  const columnItems = [];
  const classes = columnClassNames || {};
  if (!suppressTitle && item.title) {
    columnItems.push(
      <text.span as='title' key={ (item.id || '') + index }>
        <ColumnItem id={ item.id }>
          { item.title }
        </ColumnItem>
      </text.span>
    );
  }
  if (item.children) {
    for (let i = 0; i < item.children.length; i++) {
      const childItem = item.children[i];
      const onClicked = setActiveItem.bind(
        null,
        childItem.id
      );
      columnItems.push(
        <ColumnItem key={ (childItem.id || '') + i }>
          { linkBuilder({
            eidPrefix: 'uxp.hyd.product_nav',
            ...childItem,
            urlArgs,
            index,
            onClicked
          }) }
          { renderLinkFlag(childItem, classes) }
        </ColumnItem>
      );
    }
  }
  return columnItems;
};

const trayMenuBuilder = (item, classes, urlArgs) => {
  if (item.children) {
    const suppressSubcategoryTitle =
      item.children.length === 1 &&
      item.title &&
      item.children[0].title === item.title;

    return item.children.map((itm, i) => (
      <div key={ i }>
        { trayColumnBuilder(
          itm,
          classes,
          i,
          urlArgs,
          suppressSubcategoryTitle
        ) }
      </div>
    ));
  }

  return [];
};

const renderTrayTitle = (item, urlArgs) => {
  const title = item.title;
  if (item.leftnav && typeof item.leftnav.href === 'string') {
    return (
      <TrayTitle>
        <Link href={ item.leftnav.href } urlArgs={ urlArgs }>
          { title }
        </Link>
      </TrayTitle>
    );
  }

  return <TrayTitle>{ title }</TrayTitle>;
};

const trayMenusBuilder = (items = [], options = {}) => {
  const className = classNames(options.className).filter(Boolean).join(' ');
  const classes = options.classes || {};
  const result = [];
  // eslint-disable-next-line complexity
  items.forEach((item, i) => {
    if (item.children && item.children.length > 0) {
      let learnMore, description;

      const filteredChildren = item.children.filter(
        itm => itm.children && itm.children.length > 0
      );

      if (item.leftnav) {
        if (item.leftnav.description) {
          description = (
            <p>
              <FormattedMessage id={ item.leftnav.description } />
            </p>
          );
        } else if (item.leftnav.descriptionText) {
          description = <p>{ item.leftnav.descriptionText }</p>;
        }
        if (item.leftnav.btn || item.leftnav.btnText) {
          learnMore = (
            <Link
              href={ item.leftnav.href }
              rel='nofollow'
              urlArgs={ options.urlArgs }
              data-version='2400'
              className='ux-button ux-button-secondary ux-button-small learn-more'
              data-eid={ golfToEid(item.leftnav.btn || item.leftnav.btnText, {
                prefix: 'uxp.hyd.product_nav',
                postfix: 'learn_more.click'
              }) }>
              { item.leftnav.btnText || (
                <FormattedMessage id={ item.leftnav.btn } />
              ) }
            </Link>
          );
        }
      }

      const trayId = (item.id || '') + i;

      result.push(
        <li
          id={ item.id }
          key={ trayId }
          className={ className }>
          <TrayMenu
            name={ item.title }
            open={ Boolean(options.openTrays && options.openTrays[trayId]) }
            type='base'
            showOpenCaret={ true }
            onToggle={ options.onTrayToggle && ((open) => options.onTrayToggle(trayId, open)) }
            data-eid={ item.id && golfToEid(item.id, {
              prefix: 'uxp.hyd.product_nav',
              postfix: 'link.click'
            }) }>
            <div className='product-nav-container'>
              <div className='product-nav-row'>
                { renderTrayTitle(item, options.urlArgs) }
              </div>
              <div className='product-nav-row'>
                { (description || learnMore) && (
                  <div className='first'>
                    { description }
                    { learnMore }
                  </div>
                ) }
                <div
                  className='subnav-columns'>
                  <Columns num={ filteredChildren.length } style='vertical'>
                    { trayMenuBuilder({ ...item, children: filteredChildren }, classes, options.urlArgs) }
                  </Columns>
                </div>
              </div>
            </div>
          </TrayMenu>
        </li>
      );
    } else if (item.title) {
      result.push(
        <li
          id={ item.id }
          key={ (item.id || '') + i }
          className={ className }>
          <div className='tray-menu'>
            <div className='tray-toggle-wrapper'>
              { linkBuilder({
                ...item,
                className: 'tray-toggle topnav-nontray-btn',
                eidPrefix: 'uxp.hyd.product_nav',
                urlArgs: options.urlArgs
              }) }
            </div>
          </div>
        </li>
      );
    }
  });

  return result;
};

export {
  linkBuilder,
  linkListBuilder,
  renderLinkFlag,
  setActiveItem,
  trayMenusBuilder,
  trayColumnBuilder,
  urlMatches,
  renderTrayTitle
};
